import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Image,
  Flex,
  Badge,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Spinner,
  Center,
  Button,
  Select,
  HStack,
  useToast,
  ButtonGroup,
  IconButton
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

interface MatchDetails {
  name: string;
  dob: string;
  booking_date: string;
  top_charge: string;
  warrant_number: string | null;
  file_id: string | null;
  original_filename: string | null;
}

interface MatchData {
  image_id: number;
  image_path: string;
  raw_image_path: string;
  source: string;
  similarity: number;
  model: string;
  details: MatchDetails;
  model_results: string[];
  consensus: {
    num_models: number;
    models: string[];
    model_families: number;
    original_similarity: number;
    boost_applied: number;
  };
}

interface ConfirmedMatch {
  timestamp: string;
  user_name: string;
  user_email: string;
  submitted_face: string;
  match_data: MatchData;
}

// Model color mapping
const MODEL_COLORS: Record<string, string> = {
  'buffalo_l': 'purple',
  'antelopev2': 'teal',
  'magface': 'orange',
  'arcface': 'blue',
  'adaface': 'green',
  'curricularface': 'pink',
  'elasticface': 'cyan',
  'sphereface': 'red',
  'default': 'gray'
};

// Get color for a model
const getModelColor = (model: string): string => {
  // Extract the base model name (remove version numbers or suffixes)
  const baseModel = model.split('_')[0].toLowerCase();
  
  // Check if we have a specific color for this model
  for (const [key, value] of Object.entries(MODEL_COLORS)) {
    if (baseModel.includes(key.toLowerCase())) {
      return value;
    }
  }
  
  return MODEL_COLORS.default;
};

export const ConfirmedMatchReview: React.FC = () => {
  const [matches, setMatches] = useState<ConfirmedMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('timestamp_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 5;
  const toast = useToast();

  useEffect(() => {
    fetchConfirmedMatches();
  }, []);

  const fetchConfirmedMatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/insight/api/admin/confirmed-matches');
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (!data.matches) {
        throw new Error('Invalid response format: missing matches data');
      }
      setMatches(data.matches);
      setTotalPages(Math.ceil(data.matches.length / itemsPerPage));
    } catch (error) {
      console.error('Error fetching confirmed matches:', error);
      setError(error instanceof Error ? error.message : 'Failed to load confirmed matches');
      toast({
        title: 'Error',
        description: 'Failed to load confirmed matches',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const sortMatches = (matches: ConfirmedMatch[]) => {
    const sorted = [...matches];
    
    switch (sortBy) {
      case 'timestamp_desc':
        return sorted.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      case 'timestamp_asc':
        return sorted.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      case 'similarity_desc':
        return sorted.sort((a, b) => b.match_data.similarity - a.match_data.similarity);
      case 'similarity_asc':
        return sorted.sort((a, b) => a.match_data.similarity - b.match_data.similarity);
      case 'user_name':
        return sorted.sort((a, b) => a.user_name.localeCompare(b.user_name));
      default:
        return sorted;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      // Simple formatting for timestamp strings like "2025-02-25_22-16-34"
      return timestamp.replace('_', ' ').replace(/-/g, ':');
    } catch (e) {
      return timestamp;
    }
  };

  const formatSimilarity = (similarity: number) => {
    return (similarity * 100).toFixed(2) + '%';
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) {
    return (
      <Center p={10}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center p={10}>
        <Box textAlign="center">
          <Text color="red.500" mb={4}>{error}</Text>
          <Button colorScheme="blue" onClick={fetchConfirmedMatches}>
            Retry
          </Button>
        </Box>
      </Center>
    );
  }

  const sortedMatches = sortMatches(matches);
  const paginatedMatches = sortedMatches.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Box p={6}>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="lg">Confirmed Match Review</Heading>
        <HStack spacing={4}>
          <Select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            width="250px"
          >
            <option value="timestamp_desc">Newest First</option>
            <option value="timestamp_asc">Oldest First</option>
            <option value="similarity_desc">Highest Similarity</option>
            <option value="similarity_asc">Lowest Similarity</option>
            <option value="user_name">User Name</option>
          </Select>
          <Button colorScheme="blue" onClick={fetchConfirmedMatches}>
            Refresh
          </Button>
        </HStack>
      </Flex>

      {sortedMatches.length === 0 ? (
        <Text>No confirmed matches found.</Text>
      ) : (
        <>
          <Stack spacing={6}>
            {paginatedMatches.map((match, index) => (
              <Card key={index} variant="outline" boxShadow="md">
                <CardHeader bg="blue.50" p={4}>
                  <Flex justifyContent="space-between" alignItems="center">
                    <Box>
                      <Heading size="md">{match.user_name}</Heading>
                      <Text color="gray.600">{match.user_email}</Text>
                    </Box>
                    <Box textAlign="right">
                      <Text fontWeight="bold">{formatTimestamp(match.timestamp)}</Text>
                      <Badge colorScheme={match.match_data.similarity > 0.65 ? "green" : "yellow"}>
                        Similarity: {formatSimilarity(match.match_data.similarity)}
                      </Badge>
                    </Box>
                  </Flex>
                </CardHeader>
                <CardBody p={5}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {/* Submitted Face */}
                    <Box height="300px" display="flex" flexDirection="column">
                      <Heading size="sm" mb={2}>Submitted Face</Heading>
                      <Flex flex="1" justifyContent="center" alignItems="center" border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" position="relative">
                        <Image 
                          src={`/insight/api/admin/temp/${match.submitted_face}`}
                          alt="Submitted face"
                          objectFit="contain"
                          maxH="100%"
                          maxW="100%"
                          fallback={
                            <Center h="100%" w="100%">
                              <Text>Image not available</Text>
                            </Center>
                          }
                        />
                      </Flex>
                    </Box>
                    
                    {/* Matched Face */}
                    <Box height="300px" display="flex" flexDirection="column">
                      <Heading size="sm" mb={2}>Matched Face</Heading>
                      <Flex flex="1" justifyContent="center" alignItems="center" border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" position="relative">
                        <Image 
                          src={`/insight/api/faces/${match.match_data.image_path}`}
                          alt="Matched face"
                          objectFit="contain"
                          height="250px"
                          width="auto"
                          fallback={
                            <Center h="100%" w="100%">
                              <Text>Image not available</Text>
                            </Center>
                          }
                        />
                      </Flex>
                    </Box>
                  </SimpleGrid>
                  
                  <Divider my={4} />
                  
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {/* Match Details */}
                    <Box>
                      <Heading size="sm" mb={3}>Person Details</Heading>
                      <SimpleGrid columns={2} spacing={2}>
                        <Text fontWeight="bold">Name:</Text>
                        <Text>{match.match_data.details.name}</Text>
                        
                        <Text fontWeight="bold">DOB:</Text>
                        <Text>{match.match_data.details.dob}</Text>
                        
                        <Text fontWeight="bold">Booking Date:</Text>
                        <Text>{match.match_data.details.booking_date}</Text>
                        
                        <Text fontWeight="bold">Top Charge:</Text>
                        <Text>{match.match_data.details.top_charge}</Text>
                        
                        <Text fontWeight="bold">Source:</Text>
                        <Text>{match.match_data.source}</Text>
                      </SimpleGrid>
                    </Box>
                    
                    {/* Model Results */}
                    <Box>
                      <Heading size="sm" mb={3}>Model Results</Heading>
                      <Stat mb={3}>
                        <StatLabel>Primary Model</StatLabel>
                        <StatNumber>
                          <Badge colorScheme={getModelColor(match.match_data.model)} fontSize="md" py={1} px={2}>
                            {match.match_data.model}
                          </Badge>
                        </StatNumber>
                        <StatHelpText>
                          Similarity: {formatSimilarity(match.match_data.similarity)}
                        </StatHelpText>
                      </Stat>
                      
                      <Text fontWeight="bold" mb={1}>All Models:</Text>
                      <Flex flexWrap="wrap" gap={2}>
                        {(match.match_data?.model_results || []).map((model, idx) => (
                          <Badge key={idx} colorScheme={getModelColor(model)}>
                            {model}
                          </Badge>
                        ))}
                      </Flex>
                      
                      <Text mt={3} fontWeight="bold">
                        Model Families: {match.match_data?.consensus?.model_families || 'N/A'}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </CardBody>
              </Card>
            ))}
          </Stack>
          
          {/* Pagination Controls */}
          <Flex justifyContent="center" mt={6}>
            <ButtonGroup isAttached variant="outline">
              <IconButton 
                aria-label="Previous page" 
                icon={<ChevronLeftIcon />} 
                onClick={handlePrevPage}
                isDisabled={currentPage === 1}
              />
              <Button variant="outline">
                Page {currentPage} of {totalPages}
              </Button>
              <IconButton 
                aria-label="Next page" 
                icon={<ChevronRightIcon />} 
                onClick={handleNextPage}
                isDisabled={currentPage === totalPages}
              />
            </ButtonGroup>
          </Flex>
        </>
      )}
    </Box>
  );
}; 